import PropTypes from 'prop-types';
import React from 'react';
import { defaultTo } from 'lodash';
import ReactAutosuggest from 'react-autosuggest';
import { Input, TextHighlighter } from '@mlc/web-ui-toolkit';
import withId from '@nab/with-id';
import AutosuggestStyle from './Autosuggest.style.js';

import styled from 'styled-components';
import classnames from 'classnames';

const theme = {
	container: 'autosuggest__container',
	containerOpen: 'autosuggest__container--open',
	input: 'autosuggest__input',
	inputOpen: 'autosuggest__input--open',
	inputFocused: 'autosuggest__input--focused',
	suggestionsContainer: 'autosuggest__suggestions-container',
	suggestionsContainerOpen: 'autosuggest__suggestions-container--open',
	suggestionsList: 'autosuggest__suggestions-list',
	suggestion: 'autosuggest__suggestion',
	suggestionFirst: 'autosuggest__suggestion--first',
	suggestionHighlighted: 'autosuggest__suggestion--highlighted',
	sectionContainer: 'autosuggest__section-container',
	sectionContainerFirst: 'autosuggest__section-container--first',
	sectionTitle: 'autosuggest__section-title',
};

/**
 * A custom implementation of the Web-UI-Toolkit Autosuggest, since the toolkit
 * version did not allow us full control of the styling.
 */
export const Autosuggest = styled(
	({
		alwaysRenderSuggestions,
		focusInputOnSuggestionClick,
		formNoValidate,
		forwardedRef,
		getSectionSuggestions,
		getSuggestionValue,
		highlightFirstSuggestion,
		id,
		inputProps,
		onSuggestionHighlighted,
		onSuggestionsClearRequested,
		onSuggestionSelected,
		onSuggestionsFetchRequested,
		renderInputComponent,
		renderSectionTitle,
		renderSuggestion,
		renderSuggestionsContainer,
		shouldRenderSuggestions,
		style,
		suggestions,
		multiSection,
		value,
		className,
		...restProps
	}) => {
		const autoSuggestProps = {
			multiSection,
			alwaysRenderSuggestions,
			focusInputOnSuggestionClick,
			getSectionSuggestions,
			getSuggestionValue,
			highlightFirstSuggestion,
			id: `suggest-${id}`,
			inputProps: {
				...restProps,
				formNoValidate,
				//forwardedRef,
				id,
				value: defaultTo(value, ''),
				...inputProps,
			},
			onSuggestionHighlighted,
			onSuggestionsClearRequested,
			onSuggestionSelected,
			onSuggestionsFetchRequested,
			renderInputComponent,
			renderSectionTitle,
			renderSuggestion,
			renderSuggestionsContainer,
			shouldRenderSuggestions,
			suggestions,
		};
		return (
			<div className={classnames('Autosuggest', className)}>
				<ReactAutosuggest theme={theme} {...autoSuggestProps} />
			</div>
		);
	}
)`
	${AutosuggestStyle};
`;

const InputComponent = (
	{ ...restProps } // Input is a stateless component therefore ref should not be passed
) => <Input {...restProps} display="block" width="100%" />;

const renderSuggestion = (suggestion, { query }) => (
	<TextHighlighter
		activeIndex={0}
		highlightStyle={{ backgroundColor: 'transparent' }}
		searchWords={[query]}
	>
		{suggestion.text}
	</TextHighlighter>
);

Autosuggest.propTypes = {
	/** Set it to `true` if you'd like to render suggestions even when the input is not focused. */
	alwaysRenderSuggestions: PropTypes.bool,
	/** @ignore */
	display: PropTypes.string,
	/** Set it to `false` if you don't want Autosuggest to keep the input focused when suggestions are clicked/tapped. */
	focusInputOnSuggestionClick: PropTypes.bool,
	/** Implement it to teach Autosuggest where to find the suggestions for every section. */
	getSectionSuggestions: PropTypes.func,
	/** Implement it to teach Autosuggest what should be the input value when suggestion is clicked. */
	getSuggestionValue: PropTypes.func,
	/** @ignore */
	formNoValidate: PropTypes.bool,
	/** Set it to `true` if you'd like Autosuggest to automatically highlight the first suggestion. */
	highlightFirstSuggestion: PropTypes.bool,
	/** @ignore */
	id: PropTypes.string,
	/** @ignore */
	inputProps: PropTypes.object,
	multiSection: PropTypes.bool,
	/** Will be called every time you need to set suggestions to []. */
	onSuggestionsClearRequested: PropTypes.func,
	/** Will be called every time you need to recalculate `suggestions`. */
	onSuggestionsFetchRequested: PropTypes.func.isRequired,
	/** Will be called every time the highlighted suggestion changes. */
	onSuggestionHighlighted: PropTypes.func,
	/** Will be called every time suggestion is selected via mouse or keyboard. */
	onSuggestionSelected: PropTypes.func,
	/** Use it only if you need to customize the rendering of the input. */
	renderInputComponent: PropTypes.func,
	renderSectionTitle: PropTypes.func,
	renderSuggestion: PropTypes.func,
	/** Use it if you want to customize things inside the suggestions container beyond rendering the suggestions themselves. */
	renderSuggestionsContainer: PropTypes.func,
	/** When the input is focused, Autosuggest will consult this function when to render suggestions. Use it, for example, if you want to display suggestions when input value is at least 2 characters long. */
	shouldRenderSuggestions: PropTypes.func,
	/** These are the suggestions that will be displayed. Items can take an arbitrary shape. */
	suggestions: PropTypes.array,
	value: PropTypes.string,
};

Autosuggest.defaultProps = {
	formNoValidate: true,
	getSuggestionValue: suggestion => suggestion.text,
	renderInputComponent: InputComponent,
	renderSuggestion,
	suggestions: [],
	value: '',
};

Autosuggest.isFormField = true;

/**
 * @component
 */
export default withId(Autosuggest);
